import { useTranslation } from 'react-i18next';
import { UserContactData } from '../../types';
import { useEffect, useRef, useState } from 'react';
import { Form, Input, Modal, Spin, Switch } from 'antd';
import { postDataWithAuthToken } from '../../utils/axiosRequest';
import { alertMessage } from '../../utils/alertMessage';

type UserContactModalProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  editUserContact?: UserContactData;
  refresh?: Function;
  userId?: string;
};

const UserContactModal = ({
  visible,
  setVisible,
  editUserContact,
  refresh,
  userId,
}: UserContactModalProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const isSubscribed = useRef(true);
  const [form] = Form.useForm();

  useEffect(() => {
    return () => {
      isSubscribed.current = false;
    };
  }, []);

  useEffect(() => {
    if (isSubscribed.current && visible) {
      form.resetFields();
    }
  }, [editUserContact, visible, form]);

  const updateUserContact = async (values: any) => {
    const response = await postDataWithAuthToken('users/contact/edit', {
      ...values,
      userId: userId,
      contactId: editUserContact?.contactId,
    });
    return response;
  };

  const addUserContact = async (values: any) => {
    const response = await postDataWithAuthToken('users/contact/add', {
      ...values,
      userId: userId,
    });
    return response;
  };

  const onFinish = () => {
    form
      .validateFields()
      .then((values) => {
        try {
          if (isSubscribed.current) setLoading(true);
          if (editUserContact) {
            // Editing User Contact
            updateUserContact(values).then((response) => {
              if (response && response.goodStatus) {
                if (isSubscribed.current) setLoading(false);
                if (isSubscribed.current) setVisible(false);
                alertMessage('success', t('users.alerts.userContactEdited'));
                if (refresh) {
                  refresh();
                }
              } else {
                if (isSubscribed.current) setLoading(false);
                alertMessage(
                  'error',
                  response?.msg || t('general.noResponse'),
                  response?.data || undefined
                );
              }
            });
          } else {
            // Creating new User Contact
            addUserContact(values).then((response) => {
              if (response && response.goodStatus) {
                if (isSubscribed.current) setLoading(false);
                if (isSubscribed.current) setVisible(false);
                alertMessage('success', t('users.alerts.userContactAdded'));
                if (refresh) {
                  refresh();
                }
              } else {
                if (isSubscribed.current) setLoading(false);
                alertMessage(
                  'error',
                  response?.msg || t('general.noResponse'),
                  response?.data || undefined
                );
              }
            });
          }
        } catch (err) {
          console.log(err);
          if (isSubscribed.current) setLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onCancel = () => {
    if (isSubscribed.current) {
      setVisible(false);
      form.resetFields();
    }
  };

  return (
    <Modal
      visible={visible}
      title={
        editUserContact
          ? t('users.userContactColumn.editTitle')
          : t('users.userContactColumn.addTitle')
      }
      onOk={onFinish}
      onCancel={onCancel}
      bodyStyle={{ overflow: 'auto', maxHeight: 600 }}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={editUserContact}
          autoComplete="off"
        >
          <Form.Item
            name="firstName"
            label={t('users.userContactColumn.firstName')}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="lastName"
            label={t('users.userContactColumn.lastName')}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="mobile"
            label={t('users.userContactColumn.mobile')}
            dependencies={['email']}
            rules={[
              {
                validator: (_, value, callback) => {
                  const email = form.getFieldValue('email');
                  if (!value && !email) {
                    return Promise.reject(
                      new Error(t('general.inputError.mobileOrEmailRequired'))
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('users.userContactColumn.email')}
            dependencies={['mobile']}
            rules={[
              {
                type: 'email',
                message: t('general.inputError.pleaseInputValidEmail'),
              },
              {
                validator: (_, value, callback) => {
                  const mobile = form.getFieldValue('mobile');
                  if (!value && !mobile) {
                    return Promise.reject(
                      new Error(t('general.inputError.mobileOrEmailRequired'))
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="position"
            label={t('users.userContactColumn.position')}
          >
            <Input />
          </Form.Item>

          <Form.Item name="remark" label={t('users.userContactColumn.remark')}>
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>

          <Form.Item
            name="isDefault"
            valuePropName="checked"
            label={t('users.userContactColumn.setIsDefault')}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default UserContactModal;
